import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    OnDestroy,
    OnInit,
    signal,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TelXLButtonComponent } from '@dxp/elements';
import { catchError, concatMap, finalize, from, of, take, tap } from 'rxjs';
import { v4 as uuidV4 } from 'uuid';
import { IFileEntry } from '@dxp/core/models';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import {
    ITaggedUploadEvent,
    PageLockService,
    UploadBusEvent,
    UploadService,
} from '@dxp/core/services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    ConnectedPosition,
    Overlay,
} from '@angular/cdk/overlay';
import { UploadState } from '@dxp/core/types';
import { TelXLOverlayComponent } from '../overlay/overlay.component';
import { OverlayRef } from '../overlay/overlay.ref';
import { TelXLDropzonePanelComponent } from './dropzone/dropzone-panel.component';
import { TelXLFilePanelComponent } from './file/file-panel.component';
import { TelXLUploadingPanelComponent } from './uploading/uploading-panel.component';
import { ConfirmationPanelComponent } from './confirmation/confirmation-panel.component';

const EMAIL_MAXIMUM_MB = 20;
const MESSAGING_MAXIMUM_MB = 20;

export interface IUploader {
    progress: number;
    remaining: number;
    filename: string;
    total: number;
    current: number;
    complete: number;
}

@Component({
    selector: 'telxl-shared-upload',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        CdkOverlayOrigin,
        CdkConnectedOverlay,
        NgbTooltip,
        TelXLOverlayComponent,
        TelXLDropzonePanelComponent,
        TelXLButtonComponent,
        TelXLFilePanelComponent,
        TelXLUploadingPanelComponent,
        ConfirmationPanelComponent,
    ],
    templateUrl: './upload-panel.component.html',
    styleUrl: './upload-panel.component.scss',
})
export class TelXLUploadPanelComponent implements OnInit, OnDestroy {
    @ViewChild('closeBtn', { read: ElementRef })
    closeBtn!: ElementRef<HTMLElement>;

    readonly overlay = inject(Overlay);
    readonly #destroyRef = inject(DestroyRef);
    readonly #overlayRef = inject(OverlayRef<IFileEntry[] | undefined>);
    readonly #uploadService = inject(UploadService);
    readonly #pagelockService = inject(PageLockService);

    readonly #fileEntries = signal<IFileEntry[]>([]);

    target = input.required<'email' | 'messaging'>();
    files = input<IFileEntry[]>([]);

    acceptedMessagingTypes = [
        '.doc',
        '.docx',
        '.ppt',
        '.pptx',
        '.xls',
        '.xlsx',
        '.pdf',
        '.csv',
        '.gif',
        '.jpeg',
        '.jpg',
        '.jpe',
        '.bmp',
        '.tiff',
        '.tif',
        '.txt',
        '.rtf',
        '.mp3',
        '.png',
        '.mp4',
        '.m4v',
        '.3gp',
        '.3g2',
        '.aac',
        '.amr',
        '.3ga',
        '.ogg',
    ];

    acceptedEmailTypes = [
        '.doc',
        '.docx',
        '.ppt',
        '.pptx',
        '.xls',
        '.xlsx',
        '.pdf',
        '.csv',
        '.gif',
        '.jpeg',
        '.jpg',
        '.jpe',
        '.bmp',
        '.tiff',
        '.tif',
        '.txt',
        '.rtf',
        '.mp3',
        '.png',
        '.mp4',
        '.m4v',
        '.3gp',
        '.3g2',
        '.aac',
        '.amr',
        '.3ga',
        '.ogg',
    ].join(',');

    readonly positions: ConnectedPosition[] = [
        {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
            offsetX: -8,
            offsetY: -8,
        },
        {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom',
            offsetX: -12,
            offsetY: -12,
        },
        {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
            offsetY: 8,
        },
    ];

    readonly uploader = signal<IUploader>({
        progress: 0,
        remaining: 0,
        filename: '',
        total: 0,
        current: 0,
        complete: 0,
    });

    readonly originReady = signal(false);
    readonly removedCommited = signal<boolean>(false);
    readonly confirmationOpen = signal<boolean>(false);

    readonly maximumSize = computed<number>(() =>
        this.target() === 'email'
            ? 1024 * 1024 * EMAIL_MAXIMUM_MB
            : 1024 * 1024 * MESSAGING_MAXIMUM_MB,
    );

    readonly maximum = computed<string>(() =>
        this.target() === 'email'
            ? EMAIL_MAXIMUM_MB.toString()
            : MESSAGING_MAXIMUM_MB.toString(),
    );

    readonly state = computed<UploadState>(() => {
        for (const file of this.#fileEntries()) {
            if (file.state === 'unsupported') {
                return 'unsupported';
            }

            if (file.state === 'toolarge') {
                return 'toolarge';
            }

            if (file.state === 'duplicate') {
                return 'duplicate';
            }
        }

        return this.totalSize() > this.maximumSize() ? 'toolarge' : 'upload';
    });

    readonly totalSize = computed<number>(() =>
        this.uploaded().reduce((total: number, file: IFileEntry) => {
            return total + file.file.size;
        }, 0),
    );

    readonly total = computed<number>(() =>
        Math.round(this.totalSize() / 1024 / 1024),
    );

    readonly remainingSize = computed<number>(
        () => this.maximumSize() - this.totalSize(),
    );

    readonly remaining = computed<number>(() =>
        Math.round(this.remainingSize() / 1024 / 1024),
    );

    readonly uploading = computed<IFileEntry[]>(() =>
        this.#fileEntries().filter(
            f => f.state === 'queued' || f.state === 'uploading',
        ),
    );

    readonly uploaded = computed<IFileEntry[]>(() =>
        this.#fileEntries().filter(f => f.state === 'uploaded'),
    );

    readonly issues = computed<IFileEntry[]>(() =>
        this.#fileEntries().filter(
            f =>
                f.state !== 'uploaded' &&
                f.state !== 'queued' &&
                f.state !== 'uploading',
        ),
    );

    readonly hasChanges = computed<boolean>(
        () =>
            this.removedCommited() ||
            this.#fileEntries().filter(f => !f.commited).length > 0,
    );

    constructor() {
        afterNextRender(() => this.originReady.set(true));
    }

    ngOnInit() {
        this.#pagelockService.enable();
        this.#fileEntries.set(this.files());
    }

    ngOnDestroy() {
        this.#pagelockService.disable();
    }

    onFilesDropped(files: File[]) {
        const idMap = new Map<File, string>();
        const toAppend: IFileEntry[] = [];

        const existingKeys = new Set(
            this.#fileEntries().map(e => this.#fileKey(e.file)),
        );

        for (const file of files) {
            const key = this.#fileKey(file);
            const id = uuidV4();
            idMap.set(file, id);

            if (existingKeys.has(key)) {
                toAppend.push({ id, file, progress: 0, state: 'duplicate' });
            } else if (this.#isUnsupported(file)) {
                toAppend.push({ id, file, progress: 0, state: 'unsupported' });
            } else if (this.#isTooLarge(file)) {
                toAppend.push({ id, file, progress: 0, state: 'toolarge' });
            } else {
                toAppend.push({ id, file, progress: 0, state: 'queued' });
            }
        }

        if (!toAppend.length) return;

        this.#fileEntries.update(prev => [...prev, ...toAppend]);

        const newFiles = toAppend
            .filter(f => f.state === 'queued')
            .map(e => e.file);

        this.uploader.set({
            progress: 0,
            remaining: newFiles.reduce((total: number, file: File) => {
                return total + file.size;
            }, 0),
            filename: '',
            total: newFiles.length,
            current: 0,
            complete: 0,
        });

        this.#uploadService
            .uploadManyJoditSequential(
                newFiles,
                file => idMap.get(file) as string,
            )
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((evt: UploadBusEvent) => {
                if ('type' in evt && evt.type === 'overall') {
                    this.uploader.update(curr => ({
                        ...curr,
                        progress: evt.progress,
                        remaining: evt.etaSeconds ?? 0,
                    }));
                    return;
                }

                const fileEvt = evt as ITaggedUploadEvent;
                const s = fileEvt.status;
                this.#fileEntries.update(list => {
                    const idx = list.findIndex(e => e.id === fileEvt.id);
                    if (idx === -1) return list;

                    const row = list[idx];

                    let patch: Partial<IFileEntry>;
                    if (s.type === 'progress') {
                        this.uploader.update(curr => ({
                            ...curr,
                            filename: row.file.name ?? '',
                            current:
                                row.file.name !== curr.filename
                                    ? curr.current + 1
                                    : curr.current,
                        }));

                        patch = {
                            progress: s.progress,
                            remaining: s.etaSeconds ?? undefined,
                            state: 'uploading',
                        };
                    } else if (s.type === 'success') {
                        this.uploader.update(curr => ({
                            ...curr,
                            complete: curr.complete + 1,
                        }));

                        patch = {
                            progress: 100,
                            remaining: 0,
                            url: s.url,
                            response: s.response,
                            error: undefined,
                            state: 'uploaded',
                        };
                    } else {
                        this.uploader.update(curr => ({
                            ...curr,
                            complete: curr.complete + 1,
                        }));

                        patch = { error: s.error, state: 'failed' };
                    }

                    const next = [...list];
                    next[idx] = { ...row, ...patch };
                    return next;
                });
            });
    }

    onFileDeleted(file: IFileEntry) {
        // If this file is a duplicate remove the not uploaded version.
        const duplicate = this.#fileEntries().find(
            f =>
                this.#fileKey(f.file) === this.#fileKey(file.file) &&
                f.state === 'duplicate',
        );

        if (duplicate) {
            file = duplicate;
        }

        // Only 'hard' delete files uploaded in this session (not committed).
        if (
            file.state === 'uploaded' &&
            file.response?.success &&
            !file.commited
        ) {
            const { directory, filename } = this.#uploadService.splitDirAndFile(
                file.response.data.files[0],
            );
            this.#uploadService
                .removeJoditFile(directory, filename)
                .pipe(
                    tap(_ =>
                        this.#fileEntries.update(curr =>
                            curr.filter(f => f.id !== file.id),
                        ),
                    ),
                    takeUntilDestroyed(this.#destroyRef),
                )
                .subscribe();
        } else {
            this.#fileEntries.update(curr =>
                curr.filter(f => f.id !== file.id),
            );

            if (file.commited) {
                this.removedCommited.set(true);
            }
        }
    }

    close(force: boolean) {
        if (this.hasChanges() && !force) {
            this.confirmationOpen.set(true);
        } else {
            const uploadedFiles = this.#fileEntries().filter(
                f => !f.commited && f.state === 'uploaded',
            );

            if (uploadedFiles.length > 0) {
                from(uploadedFiles)
                    .pipe(
                        concatMap(file => {
                            const { directory, filename } =
                                this.#uploadService.splitDirAndFile(
                                    file.response?.data.files[0] ?? '',
                                );
                            return this.#uploadService
                                .removeJoditFile(directory, filename)
                                .pipe(
                                    take(1),
                                    catchError(_ => of(void 0)),
                                );
                        }),
                        takeUntilDestroyed(this.#destroyRef),
                        finalize(() => {
                            this.#overlayRef.close(undefined);
                        }),
                    )
                    .subscribe();
            } else {
                this.#overlayRef.close(undefined);
            }
        }
    }

    commit() {
        this.#overlayRef.close(
            this.#fileEntries().filter(f => f.state === 'uploaded'),
        );
    }

    dismiss() {
        this.confirmationOpen.set(false);
    }

    #fileKey(file: File): string {
        return `${file.name}:${file.size}:${file.lastModified}`;
    }

    #isTooLarge(file: File): boolean {
        return file.size > this.maximumSize();
    }

    #isUnsupported(file: File): boolean {
        const parts = file.name.split('.');
        const extension = parts.length > 1 ? parts.pop()?.toLowerCase() : '';

        return this.target() === 'email'
            ? !this.acceptedEmailTypes.includes(`.${extension}`)
            : !this.acceptedMessagingTypes.includes(`.${extension}`);
    }

    get sanitizedRemaining(): string {
        const remaining = this.remaining();

        if (remaining > 1) return remaining.toString();
        if (remaining < 0) return '0';
        return '<1';
    }
}
